import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import { AtonUnit, FaultSearchDto } from "~/api/GeneratedApi";
import AtoNFaultsTab from "~/pages/aton-units-page/tabs/AtoNFaultsTab";
import {
  LocationAndResponsibilityForm,
  locationAndResponsibilityFormDefaults,
  LocationAndResponsibilityFormProps,
} from "../AtonSearchFormContainers/LocationAndResponsibilityForm";
import {
  FaultForm,
  faultFormDefaults,
  FaultFormDefaultTypes,
} from "../FaultInformationSearchContainers/FaultForm";
import {
  faultRelatedActivityDefaults,
  FaultRelatedActivityForm,
  FaultRelatedActivityFormTypes,
} from "../FaultInformationSearchContainers/FaultRelatedActivityForm";
import { GenericSearchPage } from "../GenericSearchPage";
import { useFaultSearch } from "../hooks/useFaultSearch";
import { useSearchOptions } from "../hooks/useSearchOptions";
import { SharedFormProps } from "../WaterTrafficSignContainers/WaterTrafficSignStateForm";
import { NullCheck } from "./SecurityDeviceSearchTab";

export type AtonsFaultSearchForm = FaultFormDefaultTypes &
  FaultRelatedActivityFormTypes &
  LocationAndResponsibilityFormProps & {
    count?: number;
    startPosition?: number;
  };

const defaultValues: AtonsFaultSearchForm = {
  // Vikatiedot
  ...faultFormDefaults,

  // vikaan liittyvä toiminta
  ...faultRelatedActivityDefaults,

  // sijainti ja vastuu

  ...locationAndResponsibilityFormDefaults,
};

const formToDto = (
  formData: AtonsFaultSearchForm | undefined,
): NullCheck<FaultSearchDto> | undefined => {
  if (!formData) return undefined;

  return {
    ...formData,
    operational: formData.operational ? formData.operational === "true" : null,
    faultDaysMin: Number(formData.faultDaysMin) ?? 0,
    faultDaysMax: Number(formData.faultDaysMax) ?? 0,
  };
};

export const AtonsFaultSearcTab = ({
  isDisabled,
}: SharedFormProps): JSX.Element => {
  const { t } = useTranslation("faultSearch");

  const { searchOptions, setSearchOptions } =
    useSearchOptions<AtonsFaultSearchForm>();

  const { faults, isLoading } = useFaultSearch({
    options: formToDto(searchOptions),
  });

  const searchForms = React.useMemo(
    () => [
      {
        title: t("faultInformation"),
        children: <FaultForm isDisabled={isDisabled} />,
      },
      {
        title: t("faultRelatedActivity"),
        children: <FaultRelatedActivityForm isDisabled={isDisabled} />,
      },
      {
        title: t("atonLocationAndRenponsibility"),
        children: <LocationAndResponsibilityForm isDisabled={isDisabled} />,
      },
    ],
    [isDisabled, t],
  );

  const atonUnits = React.useMemo(() => {
    const units: AtonUnit[] = [];
    faults?.forEach((a) => {
      if (a.atonUnit !== undefined) units.push(a.atonUnit);
    });
    return units;
  }, [faults]);

  const csvData = React.useMemo(() => {
    if (!faults) return [];
    return faults.map((f) => {
      return {
        Havaittu: dayjs(f.observationTimestamp).format("DD.MM.YYYY"),
        Kirjattu: dayjs(f.entryTimestamp).format("DD.MM.YYYY"),
        Vika: f.type,
        Nro: f.atonUnit?.atonNumber,
        Nimi: f.atonUnit?.name,
        lisätieto: f.memo,
        tila: f.state,
        Toimii: !f.atonUnitNonfunctional,
        Havaitsija: f.observerName,
        Korjattu: dayjs(f.fixedTimestamp).format("DD.MM.YYYY"),
      };
    });
  }, [faults]);

  return (
    <GenericSearchPage<AtonsFaultSearchForm>
      isDisabled={isDisabled}
      searchContainers={searchForms}
      defaultValues={defaultValues}
      tableContent={<AtoNFaultsTab faultsData={faults ?? []} />}
      pageTitle={t("faultSearch:faultSearchTitle")}
      isLoading={isLoading}
      setSearchOptions={setSearchOptions}
      atonUnits={atonUnits}
      csvData={csvData}
    />
  );
};
